import type {
  StateOfSalesV2ForecastMovementCategoryKey,
  StateOfSalesV2ForecastMovementDeal,
} from "../lib/risk";
import { stateOfSalesV2Colors } from "./visualTokens";

export type ForecastMovementRow = {
  key: string;
  label: string;
  value: number | null;
  base: number;
  barValue: number;
  color: string;
  kind: "total" | "change" | "unavailable";
  deals: StateOfSalesV2ForecastMovementDeal[];
};

export type ForecastMovementValues = {
  newDeals: number;
  closedWon: number;
  closedLost: number;
  movedIn: number;
  movedOut: number;
  improvedOutlook: number;
  weakenedOutlook: number;
};

export type ForecastMovementAdditionalRow = { key: string; label: string; value: number };

export type ForecastMovementDealsByCategory = Record<
  StateOfSalesV2ForecastMovementCategoryKey,
  StateOfSalesV2ForecastMovementDeal[]
>;

/** Chart geometry only: nonnegative waterfalls start exactly at zero. */
export function forecastMovementDomain(rows: readonly ForecastMovementRow[]): [number, number] {
  const chartValues = rows.flatMap(row => [row.base, row.base + row.barValue]);
  const minimum = Math.min(0, ...chartValues);
  const maximum = Math.max(0, ...chartValues);
  const span = Math.max(maximum - minimum, 1);
  const headroom = Math.max(span * 0.12, maximum * 0.04, 1);
  return [minimum < 0 ? minimum - headroom * 0.2 : 0, maximum + headroom];
}

export function buildForecastMovementRows(
  startForecast: number,
  currentForecast: number,
  values?: ForecastMovementValues | null,
  dealsByCategory?: ForecastMovementDealsByCategory | null,
  additionalRows: ForecastMovementAdditionalRow[] = [],
): ForecastMovementRow[] {
  const categories = [
    ["new_deals", "New deals", values?.newDeals ?? null],
    ["closed_won", "Won deals", values?.closedWon ?? null],
    ["closed_lost", "Lost deals", values?.closedLost ?? null],
    ["moved_in", "Moved in", values?.movedIn ?? null],
    ["moved_out", "Moved out", values?.movedOut ?? null],
    ["improved_outlook", "Improved outlook", values?.improvedOutlook ?? null],
    ["weakened_outlook", "Weakened outlook", values?.weakenedOutlook ?? null],
  ] as const;
  let cumulative = startForecast;
  const categoryRows = categories.map(([key, label, value]) => {
    if (value === null) {
      return {
        key,
        label,
        value: null,
        base: cumulative,
        barValue: 0,
        color: stateOfSalesV2Colors.neutral,
        kind: "unavailable" as const,
        deals: dealsByCategory?.[key] ?? [],
      };
    }

    const nextCumulative = cumulative + value;
    const row = {
      key,
      label,
      value,
      base: Math.min(cumulative, nextCumulative),
      barValue: Math.abs(value),
      color: value >= 0 ? stateOfSalesV2Colors.positive : stateOfSalesV2Colors.negative,
      kind: "change" as const,
      deals: dealsByCategory?.[key] ?? [],
    };
    cumulative = nextCumulative;
    return row;
  });
  const additional = additionalRows.map(item => {
    const next = cumulative + item.value;
    const row: ForecastMovementRow = { ...item, base: Math.min(cumulative, next), barValue: Math.abs(item.value),
      color: item.value >= 0 ? stateOfSalesV2Colors.positive : stateOfSalesV2Colors.negative, kind: "change", deals: [] };
    cumulative = next;
    return row;
  });
  const unclassifiedChange = Math.abs(currentForecast - cumulative) < 1e-7 ? 0 : currentForecast - cumulative;
  const unclassifiedRow: ForecastMovementRow[] = unclassifiedChange === 0
    ? []
    : [{
      key: "unclassified",
      label: "Unclassified change",
      value: unclassifiedChange,
      base: Math.min(cumulative, currentForecast),
      barValue: Math.abs(unclassifiedChange),
      color: unclassifiedChange >= 0
        ? stateOfSalesV2Colors.positive
        : stateOfSalesV2Colors.negative,
      kind: "change" as const,
      deals: dealsByCategory?.unclassified ?? [],
    }];

  return [
    {
      key: "start",
      label: "Forecast 30 days ago",
      value: startForecast,
      base: 0,
      barValue: startForecast,
      color: stateOfSalesV2Colors.anchor,
      kind: "total" as const,
      deals: [],
    },
    ...categoryRows,
    ...additional,
    ...unclassifiedRow,
    {
      key: "current",
      label: "Current Forecast",
      value: currentForecast,
      base: 0,
      barValue: currentForecast,
      color: stateOfSalesV2Colors.anchor,
      kind: "total" as const,
      deals: [],
    },
  ];
}
